import clsx from 'clsx'
import React, { useEffect, useRef } from 'react'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import postToSheet from '../../function/postToSheet'
import sendEmail from '../../function/sendEmail'
import StepFour from './FormAtoms/StepFour'
import StepOne from './FormAtoms/StepOne'
import StepThree from './FormAtoms/StepThree'
import StepTwo from './FormAtoms/StepTwo'
import autoAnimate from '@formkit/auto-animate'
import GuidanceStep from './FormAtoms/GuidanceStep'
import { twMerge } from 'tailwind-merge'
import Button from '../Button/Button'

const Form = ({ step, setStep, data, goBack, subStep, setSubStep, steps }) => {
    const [submitted, setSubmitted] = useState(false)
   
    
    const methods = useForm({ defaultValues: { useCase: '' } })
    const [loading, setLoading] = useState(false)
    const formRef = useRef()
    const stepRef = useRef()
    const { handleSubmit, trigger } = methods || {}
    const isLastStep = step === steps.length + 1;
    const isHubspotForm = steps?.[step - 1]?.name === 'hubspotForm'
    function flattenAndAverageObject(obj) {
        const flatObject = {}

        // Helper function to flatten the object recursively and calculate averages
        function flatten(current, prefix) {
            const scores = [] // Array to store scores for calculating averages

            // Iterate through each key in the current object
            for (const key in current) {
                if (current.hasOwnProperty(key)) {
                    // If the value is an object, recurse further
                    if (
                        typeof current[key] === 'object' &&
                        current[key] !== null
                    ) {
                        const childScores = flatten(
                            current[key],
                            `${prefix}${key}-`
                        )
                        if (childScores.length > 0) {
                            // Calculate the average of the scores for this section
                            const sum = childScores.reduce(
                                (acc, val) => acc + parseInt(val),
                                0
                            )
                            const average = sum / childScores.length
                            flatObject[`${prefix}${key}-average`] =
                                average.toFixed(2) // Store average, rounding to nearest whole number
                        }
                    } else {
                        // Construct new key with the prefix and set the value
                        flatObject[`${prefix}${key}`] = current[key]
                        scores.push(current[key]) // Add score to array for average calculation
                    }
                }
            }
            return scores // Return scores for parent to calculate average if needed
        }

        // Start the flattening and averaging process from the top-level object
        flatten(obj, '')

        return flatObject
    }

    /**
     * The function calculates the average of an array of numbers and returns a corresponding maturity
     * level based on the average value.
     */
    function calculateAverage(array) {
        // Sum all numbers in the array
        const sum = array.reduce(
            (accumulator, currentValue) => accumulator + currentValue,
            0
        )
        // Divide the sum by the number of elements in the array
        const average = sum / array.length

        if (average < 2) {
            return 'Initial'
        } else if (average < 3) {
            return 'Managed'
        } else if (average < 4) {
            return 'Defined'
        } else if (average < 5) {
            return 'Quantitatively Managed'
        } else {
            return 'Optimised'
        }
    }
    const submitHubspotForm = () => {
        
        const submitButton = formRef?.current?.querySelector(
            'input.hs-button[type="submit"]'
        )
        if (submitButton) {
            submitButton.click() // Click the submit button if it exists
            setTimeout(() => {
                setSubmitted(true)
            },2000)
        }
    }

    /**
     * The function `createdDate` formats the current date and time in UK timezone with a 24-hour clock and
     * returns it in a specific format.
     */
    const createdDate = () => {
        // Create a new Date object
        const currentDate = new Date()
        
        // Options to specify the time format
        const timeOptions = {
            timeZone: 'Europe/London', // UK timezone
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false // For 24-hour clock
        }

        // Get the components of the date
        const day = currentDate.toLocaleString('en-GB', {
            day: '2-digit',
            timeZone: 'Europe/London'
        })
        const month = currentDate.toLocaleString('en-GB', {
            month: 'short',
            timeZone: 'Europe/London'
        })
        const year = currentDate.toLocaleString('en-GB', {
            year: 'numeric',
            timeZone: 'Europe/London'
        })

        // Format the time using Intl.DateTimeFormat
        const formattedTime = new Intl.DateTimeFormat(
            'en-GB',
            timeOptions
        ).format(currentDate)

        // Combine the date and time parts into the desired format
        const formattedResult = `${month} ${day} ${year} at ${formattedTime}`

        return formattedResult
    }

    /**
     * The function `getHubspotFormData` retrieves form data for a HubSpot submission, ensuring required
     * fields are filled before submitting.
     */
    const getHubspotFormData = () => {
        const firstName = formRef.current.querySelector(
            'input[name="firstname"]'
        )?.value
        const lastName = formRef.current.querySelector(
            'input[name="lastname"]'
        )?.value
        const email = formRef.current.querySelector(
            'input[name="email"]'
        )?.value
        const jobtitle = formRef.current.querySelector(
            'input[name="jobtitle"]'
        )?.value
        const industry = formRef.current.querySelector(
            'select[name="0-2/industry"]'
        )?.value
        const company = formRef.current.querySelector(
            'input[name="company"]'
        )?.value
        const phone = formRef.current.querySelector(
            'input[name="phone"]'
        )?.value
        const legalConsent = formRef.current.querySelector(
            'input[name="LEGAL_CONSENT.processing"]'
        )?.checked

        const legalSubscription = formRef.current.querySelector(
            'input[name="LEGAL_CONSENT.subscription_type_1210170"]'
        )?.checked

        const hasError = formRef?.current.querySelector('.hs-error-msg')

        return { firstName, lastName, email, jobtitle, industry, company }
    }

    /**
     * The onSubmit function handles form submission by processing data, calculating averages, sending
     * emails, posting to a sheet, and handling errors.
     */
    const onSubmit = async (data) => {
        setLoading(true);

        const hubspotFormData = getHubspotFormData()
        if (!hubspotFormData) {
            setLoading(false)
            return
        }

        const flatternData = flattenAndAverageObject(data)
        const averagePoints = [
            +flatternData['questionaire-govern-average'],
            +flatternData['questionaire-identify-average'],
            +flatternData['questionaire-protect-average'],
            +flatternData['questionaire-detect-average'],
            +flatternData['questionaire-respond-average'],
            +flatternData['questionaire-recover-average']
        ]

        const maturity = calculateAverage(averagePoints)

        try {
            await postToSheet({
                fields: { ...flatternData, 'submit-date': createdDate() }
            })

            submitHubspotForm()

            window.dataLayer.push({
                event: 'assessment_contact_submit',
                eventCategory: 'assessment_form_submit'
            })
        } catch (error) {
            setLoading(false)
            // Handle errors from sendEmail
            alert(`The request takes too long to send. Please try again`)
            console.log('error', error)
            // Always run setLoading to false regardless of success or failure
            return // Stop further execution in case of error
        } finally {
            // Send email
            sendEmail({
                ...hubspotFormData,
                points: averagePoints,
                maturity,
                useCase: flatternData['useCase']
            })
        }
    }

    const triggerValidation = async (e) => {
        e.preventDefault()
        const currentStep = steps[step - 1]

        // Handle substeps if they exist
        if (currentStep.substeps) {
            // Move to the next step if the subStep index is out of bounds
            if (subStep > currentStep.substeps.length) {
                setStep((pre) => pre + 1)
                return
            }

            // To track data for GA
            if (subStep === currentStep.substeps.length) {
                window.dataLayer.push({
                    event: 'assessment_finish',
                    eventCategory: 'assessment_form_submit'
                })
            }

            // Construct substep identifier
            const subStepName = `${currentStep.name}.${currentStep.substeps[subStep - 1].name}`
            const isValidSubStep = await trigger(subStepName)

            // Move to the next step or substep based on validation and index
            if (isValidSubStep) {
                subStep === currentStep.substeps.length
                    ? setStep((pre) => pre + 1)
                    : setSubStep((pre) => pre + 1)
            }
            return
        }

        // Handle validation for steps without substeps
        const isValidFields = await trigger(currentStep.name)
        if (isValidFields) {
            setStep((pre) => pre + 1)
        }
    }
    const focusOnForm = () => {
        setTimeout(() => {
            formRef.current.scrollIntoView()
        }, 500)
    }

    const goNext = (setMethod) => {
        setMethod((pre) => pre + 1)
        // focusOnForm()
    }

    useEffect(() => {
        stepRef.current && autoAnimate(stepRef.current)
    }, [stepRef])

    return (
        <div className='!relative pb-10'>
            <FormProvider {...methods}>
                <form
                    className={twMerge(
                        'container mx-auto mt-10 scroll-mt-28 text-dark_purple_2 lg:mt-16'
                    )}
                    onSubmit={
                        isLastStep ? handleSubmit(onSubmit) : triggerValidation
                    }
                >
                    <div ref={stepRef}>
                        {steps?.map((stepProps, index) => {
                            const isActive = index + 1 === step
                            const Component = stepProps.component

                            return isActive ? (
                                <Component
                                    key={index}
                                    subStep={subStep}
                                    setStep={setStep}
                                    setLoading={setLoading}
                                    {...stepProps}
                                />
                            ) : null
                        })}
                        {step <=1 &&<div
                            className={twMerge(
                                'mt-6 flex items-center justify-start lg:mt-[100px] absolute -bottom-20 lg:bottom-0 left-0',
                                step > 1 && 'justify-end'
                            )}
                        >
                            {step > 1 && (
                                <button
                                    onClick={goBack}
                                    disabled={step === 1}
                                    type="button"
                                    className="hidden rounded-full border-[2px] border-solid border-dark_purple_2 px-[46px] py-3 font-grotesk-bold text-base text-dark_purple_2 transition duration-200 hover:border-dark_purple_2 hover:bg-dark_purple_2 hover:text-white disabled:pointer-events-none disabled:invisible"
                                >
                                    Previous
                                </button>
                            )}

                            {step<3 && <button
                                className="rounded-full bg-dark_purple_2 px-[30px] py-3 font-grotesk-bold text-xl text-white transition duration-200 hover:bg-dark_purple_2/80"
                                type="submit"
                            >
                                Continue
                            </button>}
                        </div>}
                    </div>

                    { step> 1 && step <3 && (
                        <div
                            className={twMerge(
                                'mt-6 flex items-center justify-start lg:mt-[100px]',
                                step > 1 && 'justify-end'
                            )}
                        >
                            {step > 1 && (
                                <button
                                    onClick={goBack}
                                    disabled={step === 1}
                                    type="button"
                                    className="hidden rounded-full border-[2px] border-solid border-dark_purple_2 px-[46px] py-3 font-grotesk-bold text-base text-dark_purple_2 transition duration-200 hover:border-dark_purple_2 hover:bg-dark_purple_2 hover:text-white disabled:pointer-events-none disabled:invisible"
                                >
                                    Previous
                                </button>
                            )}

                            {step<3 && <button
                                className="rounded-full bg-dark_purple_2 px-[30px] py-3 font-grotesk-bold text-xl text-white transition duration-200 hover:bg-dark_purple_2/80"
                                type="submit"
                            >
                                Continue
                            </button>}
                        </div>
                    )}
                    {step ===3 && (
                        <div className='max-w-[630px] container mx-auto absolute top-[99%] left-1/2 -translate-x-1/2'>
                            <button
                                type="submit"
                                id="my-btn"
                                className="mt-5 rounded-full bg-dark_purple_2 px-[30px] py-3 font-grotesk-bold text-xl text-white transition duration-200 hover:bg-dark_purple_2/80"
                            >
                                Submit
                            </button>
                        </div>
                    )}
                </form>
            </FormProvider>
            {step>=3 && (
                <div ref={formRef} className="container mx-auto max-w-[630px]">
                    <StepThree
                        setStep={setStep}
                        setLoading={setLoading}

                        name={'hubspotForm'}
                        question={
                            'Please fill in your details to receive the full report by email'
                        }
                    />
                </div>
            )}
            
        </div>
    )
}

export default Form
